import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { usePlayerStore } from './playerStore';
import { useQueueStore } from './queueStore';
import { useMatchStore } from './matchStore';
import { DatabaseInitializer } from '../utils/databaseInit';

interface AppState {
  // 应用状态
  isInitialized: boolean;
  isInitializing: boolean;
  initializationError: string | null;
  
  // 应用操作
  initializeApp: () => Promise<void>;
  resetApp: () => Promise<void>;
  validateApp: () => Promise<{
    isValid: boolean;
    issues: string[];
  }>;
  
  // 全局错误处理
  globalError: string | null;
  setGlobalError: (error: string | null) => void;
  clearAllErrors: () => void;
}

export const useAppStore = create<AppState>()(
  subscribeWithSelector((set, get) => ({
    // 初始状态
    isInitialized: false,
    isInitializing: false,
    initializationError: null,
    globalError: null,

    // 初始化应用
    initializeApp: async () => {
      set({ isInitializing: true, initializationError: null });

      try {
        // 1. 检查数据库是否已初始化
        const isDatabaseInitialized = await DatabaseInitializer.isDatabaseInitialized();
        
        if (!isDatabaseInitialized) {
          // 2. 初始化数据库
          await DatabaseInitializer.initializeDatabase();
        }

        // 3. 加载玩家数据
        await usePlayerStore.getState().loadPlayers();
        
        // 4. 加载队列状态
        await useQueueStore.getState().loadQueue();
        
        // 5. 如果队列不存在，使用当前玩家初始化队列
        const queueState = useQueueStore.getState();
        const playerState = usePlayerStore.getState();
        
        if (!queueState.queue && playerState.players.length > 0) {
          await queueState.initializeQueue(playerState.players);
        }

        // 6. 加载比赛历史
        await useMatchStore.getState().loadMatchHistory();

        // 7. 验证应用状态
        const validation = await get().validateApp();
        if (!validation.isValid) {
          console.warn('应用初始化完成，但存在以下问题:', validation.issues);
        }

        set({
          isInitialized: true,
          isInitializing: false,
        });

      } catch (error) {
        set({
          initializationError: error instanceof Error ? error.message : '应用初始化失败',
          isInitializing: false,
          isInitialized: false,
        });
      }
    },

    // 重置应用（开发环境使用）
    resetApp: async () => {
      set({ isInitializing: true, initializationError: null });

      try {
        // 重置数据库
        await DatabaseInitializer.resetDatabase();
        
        // 重新初始化应用
        await get().initializeApp();
        
      } catch (error) {
        set({
          initializationError: error instanceof Error ? error.message : '应用重置失败',
          isInitializing: false,
        });
      }
    },

    // 验证应用状态
    validateApp: async () => {
      try {
        const validation = await DatabaseInitializer.validateDatabase();
        
        // 额外的前端状态验证
        const playerState = usePlayerStore.getState();
        const queueState = useQueueStore.getState();
        
        const issues = [...validation.issues];
        
        if (playerState.players.length === 0) {
          issues.push('没有加载到玩家数据');
        }
        
        if (!queueState.queue) {
          issues.push('队列状态未初始化');
        }
        
        if (queueState.queue && queueState.queue.players.length !== playerState.players.length) {
          issues.push('队列中的玩家数量与加载的玩家数量不匹配');
        }

        return {
          isValid: issues.length === 0,
          issues,
        };
      } catch (error) {
        return {
          isValid: false,
          issues: [`验证过程中发生错误: ${error}`],
        };
      }
    },

    // 设置全局错误
    setGlobalError: (error: string | null) => {
      set({ globalError: error });
    },

    // 清除所有错误
    clearAllErrors: () => {
      set({ globalError: null, initializationError: null });
      usePlayerStore.getState().clearError();
      useQueueStore.getState().clearError();
      useMatchStore.getState().clearError();
    },
  }))
);

// 监听各个store的错误状态，同步到全局错误
usePlayerStore.subscribe(
  (state) => state.error,
  (error) => {
    if (error) {
      useAppStore.getState().setGlobalError(`玩家管理错误: ${error}`);
    }
  }
);

useQueueStore.subscribe(
  (state) => state.error,
  (error) => {
    if (error) {
      useAppStore.getState().setGlobalError(`队列管理错误: ${error}`);
    }
  }
);

useMatchStore.subscribe(
  (state) => state.error,
  (error) => {
    if (error) {
      useAppStore.getState().setGlobalError(`比赛管理错误: ${error}`);
    }
  }
);